import React, { useState } from 'react';
import { Breadcrumbs } from '../../../components/uikit/Breadcrumbs.tsx';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import { CustomerInfoForm } from './form-components/CustomerInfoForm.tsx';
import { AddDeviceForm } from './form-components/AddDeviceForm.tsx';
import styles from './FormStyles.module.scss';
import { SubmitLoading } from './modal-components/SubmitLoading.tsx';
import {
  FailedRepairOrderDraftDto,
  RepairsOrderClient,
  RepairsOrderQuery,
  RepairsQuery,
} from 'services/api/api-client.ts';
import { queryClient } from '../../../services/api/query-client-helper.ts';

enum CreateRepairRequestStep {
  CustomerInfo = 'customerInfo',
  AddDevice = 'addDevice',
}

export type SubmitLoadingType = 'none' | 'loading' | 'success' | 'error';

export const CreateRepairRequest = () => {
  const i18n = useScopedTranslation('Page.CreateRepairRequest');
  const [step, setStep] = useState<CreateRepairRequestStep>(
    CreateRepairRequestStep.CustomerInfo,
  );
  const [submitLoadingStatus, setSubmitLoadingStatus] =
    useState<SubmitLoadingType>('none');
  const [failedDrafts, setFailedDrafts] = useState<FailedRepairOrderDraftDto[]>(
    [],
  );

  const changeSubmitStatus = (newValue: SubmitLoadingType) => {
    setSubmitLoadingStatus(newValue);
  };

  const handleDraftUpdated = (draftId?: number) => {
    if (draftId === undefined) {
      return;
    }

    setFailedDrafts((current) =>
      current.filter((failedDraft) => failedDraft.draftId !== draftId),
    );
  };

  const hideSubmitScreen = () => {
    changeSubmitStatus('none');
    setFailedDrafts([]);
    setStep(CreateRepairRequestStep.CustomerInfo);
    void queryClient.resetQueries({
      queryKey: RepairsOrderQuery.getUserOpenRepairOrderDraftsQueryKey(),
    });
  };

  const handleSubmit = async () => {
    try {
      changeSubmitStatus('loading');
      const result = await RepairsOrderClient.submitRepairOrder();

      if (result.failedDrafts.length > 0) {
        setFailedDrafts(result.failedDrafts);
        changeSubmitStatus('none');
        await queryClient.resetQueries({
          queryKey: RepairsOrderQuery.getUserOpenRepairOrderDraftsQueryKey(),
        });
        return;
      }

      void queryClient.resetQueries({
        queryKey: RepairsOrderQuery.getUserOpenRepairOrderDraftsQueryKey(),
      });

      void queryClient.resetQueries({
        queryKey: RepairsQuery.getQueryKey(),
      });

      setFailedDrafts([]);
      changeSubmitStatus('success');
    } catch {
      changeSubmitStatus('error');
    }
  };

  const goBackFromSubmitScreen = () => {
    changeSubmitStatus('none');
  };

  return (
    <div data-test-id={'create-repair-request-container'}>
      <Breadcrumbs pageName={i18n.t('title')} />
      {submitLoadingStatus === 'none' ? (
        <div className={styles.page}>
          <div className={styles.formSection}>
            {step === CreateRepairRequestStep.CustomerInfo ? (
              <CustomerInfoForm
                onNextStep={() => setStep(CreateRepairRequestStep.AddDevice)}
              />
            ) : (
              <AddDeviceForm
                onBackStep={() => {
                  setStep(CreateRepairRequestStep.CustomerInfo);
                  void queryClient.removeQueries({
                    queryKey:
                      RepairsOrderQuery.getInternalRepairOrderInvoiceAddressQueryKey(),
                  });
                }}
                handleSubmit={handleSubmit}
                failedDrafts={failedDrafts}
                onDraftUpdated={handleDraftUpdated}
              />
            )}
          </div>
        </div>
      ) : (
        <SubmitLoading
          turnOffSubmitLoader={hideSubmitScreen}
          submitLoadingStatus={submitLoadingStatus}
          handleSubmit={handleSubmit}
          goBackFromSubmitScreen={goBackFromSubmitScreen}
        />
      )}
    </div>
  );
};
